export interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  image: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  slug: string;
  content: string;
}

export const blogPosts: BlogPost[] = [
  {
    id: 1,
    title: 'How Local LLMs Are Changing BFSI Compliance in India',
    excerpt: 'Discover why banks and NBFCs are switching to private AI models that keep data on-premise, and how this impacts regulatory compliance.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop',
    author: 'Rajesh Kumar',
    date: 'May 15, 2026',
    readTime: '8 min read',
    category: 'AI & Finance',
    slug: 'local-llms-bfsi-compliance',
    content: `
      <p>The financial services industry in India is at a crossroads. On one hand, AI promises unprecedented efficiency gains. On the other, regulatory compliance — particularly data privacy — creates significant barriers to adoption.</p>
      
      <h3>The Compliance Challenge</h3>
      <p>RBI guidelines strictly regulate how financial institutions handle customer data. Cloud-based AI solutions like ChatGPT pose a fundamental problem: sensitive financial data must leave the organization's premises to reach third-party servers.</p>
      
      <h3>Enter Local LLMs</h3>
      <p>Local Large Language Models (LLMs) run entirely on an organization's own infrastructure. Tools like Ollama make it possible to run powerful AI models on standard hardware — no cloud required, no data leaves the building.</p>
      
      <h3>Real-World Impact</h3>
      <p>At HDFC Bank, the compliance team recently approved an AI pilot specifically because it used local models. The risk assessment was straightforward: zero external data transfer means zero third-party exposure.</p>
      
      <h3>Getting Started</h3>
      <p>For most BFSI organizations, the journey starts with a single use case: a private chatbot for internal policy queries. From there, the applications expand to report generation, risk analysis, and customer service automation.</p>
    `,
  },
  {
    id: 2,
    title: "From Excel to Python: A CA's Guide to Financial Automation",
    excerpt: "Learn how to automate your monthly reporting workflows using Python, Pandas, and AI — even if you've never written code before.",
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    author: 'Priya Sharma',
    date: 'May 10, 2026',
    readTime: '12 min read',
    category: 'Tutorial',
    slug: 'excel-to-python-ca-guide',
    content: `
      <p>If you can write Excel formulas, you can write Python. This is not hyperbole — the mental models are remarkably similar.</p>
      <h3>Why Make the Switch?</h3>
      <p>Excel breaks at scale. A 100MB spreadsheet crashes. A Python script processes gigabytes. Excel requires manual updates. Python runs automatically at 6 AM every day.</p>
      <h3>Your First Script</h3>
      <p>Start with pandas — it's Excel in code form. read_excel() opens your file. groupby() does pivot tables. merge() replaces VLOOKUP.</p>
      <h3>Automating Monthly Reports</h3>
      <p>Imagine a script that downloads your bank statements, categorizes transactions, and emails a P&L summary before your morning coffee. This is not futuristic — it's a 50-line Python script.</p>
      <h3>Where AI Fits In</h3>
      <p>With local LLMs, you can ask your spreadsheet questions in plain English. "What was our highest expense category in Q2?" The AI writes the query, executes it, and explains the result.</p>
    `,
  },
  {
    id: 3,
    title: 'Building a Credit Risk Model: Step-by-Step for Indian Banks',
    excerpt: 'A practical walkthrough of creating a loan default prediction model using Indian credit bureau data and RBI guidelines.',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&h=400&fit=crop',
    author: 'Arun Nair',
    date: 'May 5, 2026',
    readTime: '15 min read',
    category: 'Case Study',
    slug: 'credit-risk-model-indian-banks',
    content: `
      <p>Credit risk modeling is the backbone of responsible lending. In India, where informal credit markets coexist with formal banking, building accurate default prediction models is both a science and an art.</p>
      
      <h3>Understanding the Data Landscape</h3>
      <p>Indian banks have access to rich data sources: CIBIL scores, Equifax reports, bank statements, and GST filings. The challenge is not data scarcity — it's data integration.</p>
      
      <h3>Feature Engineering for Indian Context</h3>
      <p>Standard Western models miss India-specific signals. Seasonal income patterns for agricultural borrowers, EMI bounce history, and regional economic indicators all carry predictive power that generic models overlook.</p>
      
      <h3>Model Selection and RBI Compliance</h3>
      <p>RBI mandates that credit decisions must be explainable. Black-box models won't pass audit. Logistic regression with clear coefficients, or tree-based models with SHAP values, are the pragmatic choices.</p>
      
      <h3>From Notebook to Production</h3>
      <p>The real test is deploying your model where loan officers can use it. A Streamlit dashboard connected to your core banking system, running entirely on-premise, satisfies both usability and compliance requirements.</p>
    `,
  },
  {
    id: 4,
    title: 'RBI Guidelines on AI Governance: What Finance Teams Must Know',
    excerpt: 'Breaking down the latest RBI circulars on AI model governance, explainability requirements, and compliance frameworks.',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop',
    author: 'Rajesh Kumar',
    date: 'April 28, 2026',
    readTime: '10 min read',
    category: 'Regulation',
    slug: 'rbi-ai-governance-guidelines',
    content: `
      <p>The Reserve Bank of India's approach to AI governance has evolved rapidly. What started as broad technology risk guidelines has sharpened into specific expectations for financial institutions using machine learning.</p>
      
      <h3>The Explainability Mandate</h3>
      <p>RBI now expects that any AI-driven decision affecting a customer must be explainable in plain language. This doesn't mean every parameter must be disclosed, but the reasoning process must be reconstructible and auditable.</p>
      
      <h3>Model Risk Management</h3>
      <p>Banks must maintain model inventories, conduct periodic validation, and document drift detection procedures. The days of "set it and forget it" machine learning are over.</p>
      
      <h3>Data Localization Requirements</h3>
      <p>Customer financial data used to train AI models must remain within Indian jurisdiction. For institutions using global cloud providers, this means specific region-locking and encryption protocols.</p>
      
      <h3>Practical Compliance Steps</h3>
      <p>Start with a governance framework that documents every model's purpose, data sources, and limitations. Appoint a Model Risk Officer. And most importantly — test your explanations with actual customers before regulators do.</p>
    `,
  },
  {
    id: 5,
    title: '5 AI Tools Every FP&A Manager Should Master in 2026',
    excerpt: 'From automated forecasting to scenario modeling — the essential AI toolkit for modern financial planning and analysis.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
    author: 'Priya Sharma',
    date: 'April 20, 2026',
    readTime: '7 min read',
    category: 'Tools',
    slug: 'ai-tools-fpa-manager-2026',
    content: `
      <p>The FP&A function is undergoing its biggest transformation since the spreadsheet. AI is not replacing financial analysts — it's amplifying them. Here are five tools that should be in every FP&A manager's arsenal this year.</p>
      
      <h3>1. Automated Forecasting Engines</h3>
      <p>Tools like Prophet and Nixtla's StatsForecast handle seasonality, holidays, and trend changes automatically. What used to take days of Excel wrangling now happens in minutes with better accuracy.</p>
      
      <h3>2. Natural Language Query Interfaces</h3>
      <p>Ask your financial database questions in English: "Show me Q3 operating expenses by department versus budget." Local LLMs connected to your data warehouse make this possible without sending sensitive numbers to the cloud.</p>
      
      <h3>3. Scenario Modeling with Monte Carlo</h3>
      <p>Instead of single-point forecasts, run thousands of simulations. See the full probability distribution of outcomes. Board presentations become richer when you can say "We have an 85% probability of meeting guidance."</p>
      
      <h3>4. Anomaly Detection for Variance Analysis</h3>
      <p>AI flagging unusual variances before you even open the monthly report. Focus your analytical energy on the 5% of line items that actually need explanation.</p>
      
      <h3>5. Automated Narrative Generation</h3>
      <p>Let AI draft the first version of your management commentary. You edit for nuance and context, but the heavy lifting of describing trends and comparisons is done for you.</p>
    `,
  },
  {
    id: 6,
    title: 'UPI Fraud Detection: How AI Catches Suspicious Transactions',
    excerpt: 'An inside look at how Indian fintechs use anomaly detection to prevent UPI fraud with 95%+ accuracy rates.',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop',
    author: 'Arun Nair',
    date: 'April 15, 2026',
    readTime: '11 min read',
    category: 'Case Study',
    slug: 'upi-fraud-detection-ai',
    content: `
      <p>With over 12 billion monthly transactions, UPI is India's payment backbone — and a prime target for fraudsters. The good news: AI-powered detection systems are now catching suspicious patterns in milliseconds.</p>
      
      <h3>The Scale of the Challenge</h3>
      <p>UPI processes more transactions per month than all credit cards globally. Traditional rule-based systems can't keep up. A rule that blocks all transactions above ₹50,000 would block legitimate business payments while sophisticated fraudsters simply stay below the threshold.</p>
      
      <h3>Behavioral Biometrics</h3>
      <p>Modern fraud detection doesn't just look at what you pay — it looks at how you pay. Typing speed, device tilt, swipe patterns, and transaction timing all form a behavioral fingerprint that's nearly impossible to fake.</p>
      
      <h3>Graph Networks for Collusion Detection</h3>
      <p>Fraud rings create complex webs of mule accounts. Graph neural networks map these relationships, identifying clusters of accounts that behave suspiciously similarly even when individual transactions look normal.</p>
      
      <h3>The Human-AI Partnership</h3>
      <p>The best systems don't auto-block everything flagged. They queue high-risk transactions for human review while letting low-risk ones through instantly. This balance keeps fraud below 0.01% without frustrating legitimate users.</p>
    `,
  },
];

export const blogPostsBySlug: Record<string, BlogPost> = Object.fromEntries(
  blogPosts.map(post => [post.slug, post])
);

export const categories = ['All', 'AI & Finance', 'Tutorial', 'Case Study', 'Regulation', 'Tools'];
